import type { Client } from "@osdk/client";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { GlobalReportV2 } from "@unruly-osdk/sdk";

/**
 * Shape of global research content for rendering.
 */
export interface GlobalReportData {
  id: string;
  urlSlug?: string | null;
  reportName?: string | null;
  reportText?: string | null;
  reportSummary?: string | null;
  generatedAt?: string | null;
}

const keys = {
  all: ["globalReports"] as const,
  bySlug: (slug: string) => ["globalReports", "bySlug", slug] as const,
  first: ["globalReports", "first"] as const,
};

function GlobalReportService(_queryClient: unknown, apiClient: Client) {
  const service = {
    useGetGlobalReportBySlug(
      slug: string | undefined,
    ): UseQueryResult<GlobalReportData | null, unknown> {
      return useQuery({
        queryKey: slug ? keys.bySlug(slug) : keys.first,
        queryFn: () =>
          slug
            ? service.getGlobalReportBySlug(slug)
            : service.getFirstGlobalReport(),
        enabled: !!slug,
      });
    },

    // Returns null only when there genuinely is no report. A failure must
    // propagate, or the page shows an empty state and hides the outage.
    async getFirstGlobalReport(): Promise<GlobalReportData | null> {
      const response = await apiClient(GlobalReportV2).fetchPage({
        $orderBy: { generatedAt: "desc" },
        $pageSize: 1,
      });
      const first = response.data?.[0];
      if (!first) return null;
      return service.toGlobalReportData(first);
    },

    async getGlobalReportBySlug(
      slug: string,
    ): Promise<GlobalReportData | null> {
      const response = await apiClient(GlobalReportV2)
        .where({ urlSlug: slug })
        .fetchPage({
          $orderBy: { generatedAt: "desc" },
          $pageSize: 1,
        });
      const first = response.data?.[0];
      if (!first) return null;
      return service.toGlobalReportData(first);
    },

    toGlobalReportData(report: {
      id: string;
      urlSlug?: string | null;
      reportName?: string | null;
      reportText?: string | null;
      reportSummary?: string | null;
      generatedAt?: string | null;
    }): GlobalReportData {
      return {
        id: report.id,
        urlSlug: report.urlSlug ?? null,
        reportName: report.reportName ?? null,
        reportText: report.reportText ?? null,
        reportSummary: report.reportSummary ?? null,
        generatedAt: report.generatedAt ?? null,
      };
    },
  };
  return service;
}

export default GlobalReportService;
